import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '@/lib/api';
import OnboardingChecklist from '@/components/OnboardingChecklist';
import {
  FlaskConical, Gauge, Ruler, Building, FileText, AlertTriangle, CheckCircle, Clock,
  ClipboardList, PackageCheck, ChevronRight, Plus, MessageCircle,
} from 'lucide-react';

// Dashboard rebuild: command-center styl.
// 3 zóny: Vyžaduje pozornost (actionable) → Rychlé akce → Statistiky (sekundární).

const TONE_STYLES = {
  warning: { bg: 'var(--ct-warning-soft)', fg: 'var(--ct-warning)' },
  danger: { bg: 'var(--ct-error-soft)', fg: 'var(--ct-error)' },
  info: { bg: 'var(--ct-info-soft)', fg: 'var(--ct-info)' },
  success: { bg: 'var(--ct-success-soft)', fg: 'var(--ct-success)' },
};

function AttentionCard({ icon: Icon, tone, count, title, action }) {
  const t = TONE_STYLES[tone] || TONE_STYLES.info;
  return (
    <button
      type="button"
      onClick={action}
      className="group flex items-center gap-3.5 p-4 rounded-lg border text-left transition-all duration-200"
      style={{
        background: 'var(--ct-surface)',
        borderColor: 'var(--ct-border)',
        boxShadow: 'var(--ct-shadow-sm)',
      }}
      onMouseEnter={(e) => { e.currentTarget.style.boxShadow = 'var(--ct-shadow-md)'; e.currentTarget.style.borderColor = t.fg; e.currentTarget.style.background = t.bg; e.currentTarget.style.transform = 'translateY(-1px)'; }}
      onMouseLeave={(e) => { e.currentTarget.style.boxShadow = 'var(--ct-shadow-sm)'; e.currentTarget.style.borderColor = 'var(--ct-border)'; e.currentTarget.style.background = 'var(--ct-surface)'; e.currentTarget.style.transform = 'none'; }}
    >
      <div
        className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
        style={{ background: t.bg, color: t.fg }}
      >
        <Icon className="w-5 h-5" style={{ color: t.fg }} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-xl font-bold leading-tight tabular-nums" style={{ color: 'var(--ct-text)' }}>{count}</div>
        <div className="text-sm mt-0.5" style={{ color: 'var(--ct-text-secondary)' }}>{title}</div>
      </div>
      <ChevronRight className="w-4 h-4 flex-shrink-0 transition-transform duration-200 group-hover:translate-x-0.5" style={{ color: t.fg }} />
    </button>
  );
}

function QuickActionButton({ icon: Icon, label, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="group flex items-center gap-3 px-3 py-2.5 rounded-lg border text-sm font-semibold transition-all duration-200"
      style={{
        background: 'var(--ct-surface)',
        borderColor: 'var(--ct-border)',
        color: 'var(--ct-text)',
        boxShadow: 'var(--ct-shadow-sm)',
      }}
      onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'var(--ct-accent)'; e.currentTarget.style.boxShadow = 'var(--ct-shadow-md)'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
      onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'var(--ct-border)'; e.currentTarget.style.boxShadow = 'var(--ct-shadow-sm)'; e.currentTarget.style.transform = 'none'; }}
    >
      <span
        className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 transition-colors"
        style={{ background: 'var(--ct-accent-soft)' }}
      >
        <Icon className="w-4 h-4" style={{ color: 'var(--ct-accent)' }} />
      </span>
      <span className="truncate">{label}</span>
    </button>
  );
}

function StatTile({ icon: Icon, label, value, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="ct-stat-card text-left"
      title={`Přejít na ${label}`}
    >
      <div className="flex items-center justify-between mb-2">
        <span
          className="w-8 h-8 rounded-lg flex items-center justify-center"
          style={{ background: 'var(--ct-bg-secondary)' }}
        >
          <Icon className="w-4 h-4" style={{ color: 'var(--ct-accent)' }} />
        </span>
      </div>
      <div className="text-2xl font-bold leading-none tabular-nums" style={{ color: 'var(--ct-text)' }}>{value}</div>
      <div className="ct-eyebrow mt-1">{label}</div>
    </button>
  );
}

export default function DashboardPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [stats, setStats] = useState({});
  const [notifications, setNotifications] = useState([]);

  useEffect(() => {
    api.get('/dashboard/stats').then(r => setStats(r.data || {})).catch(() => {});
    api.get('/notifications').then(r => setNotifications(r.data)).catch(() => {});
  }, []);

  const errorNotifs = notifications.filter(n => n.type === 'error');
  const warnNotifs = notifications.filter(n => n.type === 'warning');

  // Zóna 1: Vyžaduje pozornost — jen položky s count > 0, seřazené podle urgentnosti.
  // i18next plurals: dashboard.attention.<key>_one / _few / _many / _other
  const attentionItems = [
    errorNotifs.length > 0 && {
      icon: AlertTriangle,
      tone: 'danger',
      count: errorNotifs.length,
      title: t('dashboard.attention.etalons-expired', { count: errorNotifs.length }),
      action: () => navigate('/etalons'),
    },
    stats.calibrationsPending > 0 && {
      icon: Clock,
      tone: 'warning',
      count: stats.calibrationsPending,
      title: t('dashboard.attention.calibrations-pending-approval', { count: stats.calibrationsPending }),
      action: () => navigate('/approvals'),
    },
    warnNotifs.length > 0 && {
      icon: Clock,
      tone: 'warning',
      count: warnNotifs.length,
      title: t('dashboard.attention.etalons-expiring', { count: warnNotifs.length }),
      action: () => navigate('/etalons'),
    },
    stats.ordersConfirmed > 0 && {
      icon: ClipboardList,
      tone: 'warning',
      count: stats.ordersConfirmed,
      title: t('dashboard.attention.orders-pending-intake', { count: stats.ordersConfirmed }),
      action: () => navigate('/intakes'),
    },
    stats.intakesNotSent > 0 && {
      icon: PackageCheck,
      tone: 'info',
      count: stats.intakesNotSent,
      title: t('dashboard.attention.intakes-not-sent', { count: stats.intakesNotSent }),
      action: () => navigate('/intakes'),
    },
    stats.calibrationsPendingIntake > 0 && {
      icon: FlaskConical,
      tone: 'info',
      count: stats.calibrationsPendingIntake,
      title: t('dashboard.attention.calibrations-pending-start', { count: stats.calibrationsPendingIntake }),
      action: () => navigate('/calibrations'),
    },
  ].filter(Boolean);

  // Zóna 2: Rychlé akce — navigace na nejčastější vstupní body.
  const quickActions = [
    { icon: Plus, label: t('dashboard.actions.new-order'), onClick: () => navigate('/orders') },
    { icon: PackageCheck, label: t('dashboard.actions.new-intake'), onClick: () => navigate('/intakes') },
    { icon: FlaskConical, label: t('dashboard.actions.new-calibration'), onClick: () => navigate('/calibrations') },
    { icon: MessageCircle, label: t('menu.chat'), onClick: () => navigate('/chat') },
  ];

  // Zóna 3: Sekundární statistiky — referenční data + přehled kalibrací.
  const statTiles = [
    { label: t('menu.instruments'), value: stats.instruments || 0, icon: Gauge, to: '/instruments' },
    { label: t('menu.etalons'), value: stats.etalons || 0, icon: Ruler, to: '/etalons' },
    { label: t('menu.companies'), value: stats.companies || 0, icon: Building, to: '/companies' },
    { label: t('menu.methodology'), value: stats.methodologies || 0, icon: FileText, to: '/methodology' },
    { label: t('dashboard.stats.total-calibrations'), value: stats.calibrationsTotal || 0, icon: FlaskConical, to: '/calibrations' },
    { label: t('dashboard.stats.approved'), value: stats.calibrationsApproved || 0, icon: CheckCircle, to: '/certificates' },
  ];

  return (
    <div data-testid="dashboard-page" className="space-y-6 max-w-6xl">
      <div>
        <h1 className="ct-h1">{t('menu.dashboard')}</h1>
        <p className="ct-page-subtitle">{t('dashboard.subtitle')}</p>
      </div>

      {/* Onboarding checklist — jen pro nový/prázdný lab (sám se skryje) */}
      <OnboardingChecklist />

      {/* Zóna 1: Vyžaduje pozornost */}
      <section>
        <h2 className="ct-eyebrow mb-3">{t('dashboard.attention.title')}</h2>
        {attentionItems.length === 0 ? (
          <div
            className="flex items-center gap-3 px-4 py-3 rounded-lg"
            style={{
              background: 'var(--ct-success-soft)',
              border: '1px solid var(--ct-success)',
            }}
          >
            <CheckCircle className="w-5 h-5 flex-shrink-0" style={{ color: 'var(--ct-success)' }} />
            <div>
              <div className="font-medium" style={{ color: 'var(--ct-success)' }}>{t('dashboard.attention.all-ok')}</div>
              <div className="text-xs mt-0.5" style={{ color: 'var(--ct-text-secondary)' }}>
                {t('dashboard.attention.all-ok-description')}
              </div>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {attentionItems.map((item, idx) => (
              <AttentionCard key={idx} {...item} />
            ))}
          </div>
        )}
      </section>

      {/* Zóna 2: Rychlé akce */}
      <section>
        <h2 className="ct-eyebrow mb-3">{t('dashboard.actions.title')}</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          {quickActions.map((a) => (
            <QuickActionButton key={a.label} {...a} />
          ))}
        </div>
      </section>

      {/* Zóna 3: Statistiky */}
      <section>
        <h2 className="ct-eyebrow mb-3">{t('dashboard.stats.title')}</h2>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          {statTiles.map((s) => (
            <StatTile
              key={s.label}
              icon={s.icon}
              label={s.label}
              value={s.value}
              onClick={() => navigate(s.to)}
            />
          ))}
        </div>
      </section>
    </div>
  );
}
